import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { isPresent, isType } from '@mtna/core-ts';
import { MtnaDataType } from '@mtna/data-core-ts';
import { CanDestroyNotify, mixinDestroyNotifier, MtnaLogger } from '@mtna/core-angular';
import { Classification, instanceOfVariable, Variable } from '@mtna/pojo-consumer-ts';
import { VariableSummary } from '@mtna/variable-service-core-ts';
import { RdsVariableStateFacade } from '@rds/shared-state-variable';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { RdsDataTypeUtil } from '../core/utils';

const _MixinVariableDetails = mixinDestroyNotifier(class {});

@Component({
  selector: 'rds-c-variable-details',
  templateUrl: 'variable-details.component.html',
  styleUrls: ['variable-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsVariableDetailsComponent extends _MixinVariableDetails implements CanDestroyNotify, OnInit {
  static ngAcceptInputType_variable: Variable | VariableSummary | null | undefined;

  classification$ = this.variableFacade.classification$;
  codes$ = this.variableFacade.codes$;
  codesFound$ = this.variableFacade.codesFound$;
  codesPageIndex$ = this.variableFacade.codesPageIndex$;
  codesPageSize$ = this.variableFacade.codesPageSize$;
  codesSearchText$ = this.variableFacade.codesSearchText$;
  loadingClassificationOrCodes$ = combineLatest([this.variableFacade.loadingClassification$, this.variableFacade.searchingCodes$]).pipe(
    map(([load, search]) => load || search)
  );
  codesSortColumn$ = this.variableFacade.codesSortField$.pipe(map((field) => (field === 'CODE' ? 'codeValue' : undefined)));
  codesSortDirection$ = this.variableFacade.codesSortOrder$.pipe(map((order) => (order ? (order.toLowerCase() as SortDirection) : '')));
  frequencyChart$ = this.variableFacade.chartFrequency$;
  loadingFrequencyChart$ = this.variableFacade.loadingChartFrequency$;
  isVariableSummary$ = new BehaviorSubject<boolean>(false);
  variable$ = new BehaviorSubject<Variable | VariableSummary | undefined>(undefined);

  @Input() classification: Classification | null | undefined;

  @Input()
  get variable(): Variable | VariableSummary | null | undefined {
    return this._variable;
  }
  set variable(v: Variable | VariableSummary | null | undefined) {
    this.variable$.next(v || undefined);
    if (v && !instanceOfVariable(v)) {
      this.variableFacade.load(v.uri);
      this.isVariableSummary$.next(true);
    } else {
      this.isVariableSummary$.next(false);
    }
    // The intial variable could be a summary, with a 2nd actual variable comming shortly after.
    // To prevent double classification loading, we compare classification uris
    if (v?.classificationUri && v.classificationUri !== this._variable?.classificationUri) {
      this.variableFacade.loadClassification(v.classificationUri);
      this._filterType = 'CODE';
    } else {
      this._filterType = v?.dataType ? RdsDataTypeUtil.simplifyMtnaDataType(v.dataType as MtnaDataType) : undefined;
    }
    // When a variable is selected, get its frequencies in a format to use in an amchart.
    if (isType<Variable>(v, 'frequencies') && v.frequencies?.sets?.length) {
      if (v.uri) {
        this.variableFacade.loadChartFrequency(v.uri);
      } else {
        this.logger.warn(`[VariableDetailsComponent] variable does not have a URI or ID, will not get frequency`, v);
      }
    }
    this._variable = v;
  }
  private _variable: Variable | VariableSummary | null | undefined;

  _filterType: 'CODE' | 'DATE' | 'TEXT' | 'NUMERIC' | undefined;

  constructor(private logger: MtnaLogger, private variableFacade: RdsVariableStateFacade) {
    super();
  }

  ngOnInit() {
    // Whenever a variable comes down from the state, set it as the current variable & reset the isSummary
    this.variableFacade.variable$.pipe(takeUntil(this.destroyed$), filter(isPresent)).subscribe((variable) => {
      this.variable$.next(variable);
      this.isVariableSummary$.next(false);
    });
  }

  paginateCodes(page: PageEvent) {
    this.variableFacade.pageCodes(page);
  }

  searchCodes(searchText: string) {
    this.variableFacade.searchCodes(undefined, { searchText });
  }

  sortCodes(sort: Sort) {
    const sortOrder = sort.direction?.length ? (sort.direction.toUpperCase() as 'ASC' | 'DESC') : undefined;
    this.variableFacade.searchCodes(undefined, { sortField: sortOrder ? 'CODE' : undefined, sortOrder });
  }
}
